import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { applyCallbackTelemetryHeaders } from '@shared/helpers/callbackTelemetry';
import { CommunicationRequestHeaders } from '@shared/models/callback';
import { getAccessToken } from './getAccessToken';
import { getFlowType } from './msal';
import { customerCostingBoundaryError } from './customerCostingBoundary';

export interface CommunicationFetchArgs extends FetchArgs {
  headers?: FetchArgs['headers'] | CommunicationRequestHeaders;
}

const getRequestContext = (arg: string | FetchArgs | CommunicationFetchArgs) => ({
  url: typeof arg === 'string' ? arg : arg.url,
  requestHeaders: typeof arg === 'string' ? undefined : arg.headers,
});

const baseQuery = fetchBaseQuery({
  baseUrl: `${window.env.api.link}`,
  prepareHeaders: async (headers, { arg }) => {
    const token = await getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set("Policy", getFlowType())

    return applyCallbackTelemetryHeaders(headers, getRequestContext(arg));
  },
});

const baseCommunicationQuery = fetchBaseQuery({
  baseUrl: `${window.env.api.communication}`,
  prepareHeaders: async (headers, { arg }) => {
    const token = await getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set("Policy", getFlowType())

    return applyCallbackTelemetryHeaders(headers, getRequestContext(arg));
  },
});

export const baseQueryWithMsalAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const boundaryError = customerCostingBoundaryError(args);
  if (boundaryError) {
    return { error: boundaryError };
  }

  let result = await baseQuery(args, api, extraOptions);

  switch (result.error?.status) {
    case 401: {
      // Attempt to get a fresh token and retry
      const token = await getAccessToken();
      if (token) {
        result = await baseQuery(args, api, extraOptions);
      } else {
        console.warn('Unauthorized, user might need to login again');
      }
      break;
    }
    default:
      break;
  }

  if (result.error) {
    console.error('API Error:', result.error);
  }

  return result;
};

export const baseCommunicationQueryWithMsalAuth: BaseQueryFn<string | CommunicationFetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const boundaryError = customerCostingBoundaryError(args);
  if (boundaryError) {
    return { error: boundaryError };
  }

  let result = await baseCommunicationQuery(args, api, extraOptions);

  switch (result.error?.status) {
    case 401: {
      // Attempt to get a fresh token and retry
      const token = await getAccessToken();
      if (token) {
        result = await baseCommunicationQuery(args, api, extraOptions);
      } else {
        console.warn('Unauthorized, user might need to login again');
      }
      break;
    }
    default:
      break;
  }

  if (result.error) {
    console.error('API Error:', result.error);
  }

  return result;
};
